import { Accordion, AccordionHeader, AccordionItem, AccordionPanel, Button, Combobox, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Field, Input, Option, Popover, PopoverSurface, PopoverTrigger, Spinner } from "@fluentui/react-components";
import { ColorPicker, ColorArea, ColorSlider } from "@fluentui/react-color-picker";
import { tinycolor } from "@ctrl/tinycolor";
import { useAppContext } from "../../AppContext";
import { useEffect, useRef, useState } from "react";
import { DismissRegular } from "@fluentui/react-icons";
import { useForm, Controller } from "react-hook-form";
import { User, UserUpdateDTO } from "../../models/users";
import authService from "../../services/authService";
import { updateUser } from "../../services/usersService";

type Props = {
  onClose: () => void;
  open: boolean;
};

function hexToHsv(hex: string): { h: number; s: number; v: number } {
  const { h, s, v } = tinycolor(hex).toHsv();
  return { h, s, v };
}

function hsvToHex(h: number, s: number, v: number): string {
  return tinycolor({ h, s, v }).toHexString();
}

interface ColorPickerFieldProps {
  value: string;
  onChange: (hex: string) => void;
}

function ColorPickerField({ value, onChange }: ColorPickerFieldProps) {
  const [hsv, setHsv] = useState(() =>
    value && /^#[0-9a-fA-F]{6}$/.test(value) ? hexToHsv(value) : { h: 0, s: 1, v: 1 }
  );
  const lastEmittedHex = useRef(value);

  // Only sync inward when the value changes from OUTSIDE (e.g. dialog opens with saved color),
  // not when we ourselves just called onChange.
  useEffect(() => {
    if (value !== lastEmittedHex.current && value && /^#[0-9a-fA-F]{6}$/.test(value)) {
      lastEmittedHex.current = value;
      setHsv(hexToHsv(value));
    }
  }, [value]);

  return (
    <ColorPicker
      color={hsv}
      onColorChange={(_e, data) => {
        const hex = hsvToHex(data.color.h, data.color.s, data.color.v);
        lastEmittedHex.current = hex; // mark so useEffect won't re-sync and overwrite
        setHsv(data.color);
        onChange(hex);
      }}
    >
      <ColorArea />
      <ColorSlider />
    </ColorPicker>
  );
}

interface UserSettingsFormData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  title: string;
  phoneNumber: string;
  color: string;
}

const UserSettingsDialog = (props: Props) => {
  const [user, setUser] = useState<User>();
  const [loading, setLoading] = useState(false);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);

  const { control, handleSubmit, reset, watch, formState: { errors } } = useForm<UserSettingsFormData>({
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      firstName: "",
      lastName: "",
      title: "",
      phoneNumber: "",
      color: "",
    }
  });

  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true);
      try{
        const userData = await authService.refresh();
        setUser(userData.data.user);
        reset({
          email: userData.data?.user.email || "",
          password: userData.data?.user.password || "",
          confirmPassword: "",
          firstName: userData.data?.user.firstName || "",
          lastName: userData.data?.user.lastName || "",
          title: userData.data?.user.title || "",
          phoneNumber: userData.data?.user.phoneNumber || "",
          color: userData.data?.user.color || "",
        });
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    }
    if (props.open) {
      fetchUser();
    }

  }, [props.open, reset]);

  const onSubmit = async (data: UserSettingsFormData) => {
    if (data.password && data.password !== data.confirmPassword) {
      return; // Don't submit if passwords don't match
    }
    
    if (!user?.userID) {
      console.error("User ID not found");
      return;
    }

    setLoading(true);
    try {
      const updateData: UserUpdateDTO = {
        Email: data.email,
        FirstName: data.firstName,
        LastName: data.lastName,
        Title: data.title,
        PhoneNumber: data.phoneNumber,
        Color: data.color || undefined,
      };

      // Only include Password if it has a value
      if (data.password) {
        updateData.Password = data.password;
      }

      await updateUser(user.userID, updateData);
      props.onClose();
    } catch (error) {
      console.error("Error updating user:", error);
    } finally {
      setLoading(false);
    }
  };

  return(
    <Dialog
      open={props.open}
      onOpenChange={(_event, data) => !data.open && props.onClose()}
      modalType="alert"
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle            
          action={
            <Button
              appearance="subtle"
              shape="circular"
              aria-label="close"
              icon={<DismissRegular />}
              onClick={props.onClose}
            />
          }>Inställningar</DialogTitle>
          <DialogContent>
          {loading && <Spinner />}  
          {!loading && <form id="user-settings-form" onSubmit={handleSubmit(onSubmit)}>
            <Field label="E-post:">
              <Controller
                name="email"
                control={control}
                render={({ field }) => <Input disabled {...field} />}
              />
            </Field>
            <Accordion collapsible>
              <AccordionItem value="item-1">
                <AccordionHeader>Redigera lösenord</AccordionHeader>
                <AccordionPanel>
                  <Field label="Lösenord:">
                    <Controller
                      name="password"
                      control={control}
                      render={({ field }) => <Input type="password" {...field} />}
                    />
                  </Field>
                  <Field 
                    label="Upprepa lösenord:"
                    validationMessage={errors.confirmPassword?.message}
                    validationState={errors.confirmPassword ? "error" : "none"}
                  >
                    <Controller
                      name="confirmPassword"
                      control={control}
                      rules={{
                        validate: (value) => {
                          const password = watch("password");
                          if (password && value !== password) {
                            return "Lösenorden matchar inte";
                          }
                          return true;
                        }
                      }}
                      render={({ field }) => <Input type="password" {...field} />}
                    />
                  </Field>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
            <Field label="Förnamn: ">
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
            </Field>
            <Field label="Efternamn: ">
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
            </Field>
            <Field label="Titel: ">
              <Controller
                name="title"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
            </Field>
            <Field label="Telefon: ">
              <Controller
                name="phoneNumber"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
            </Field>
            <Field label="Profilfärg:">
              <Controller
                name="color"
                control={control}
                render={({ field }) => {
                  return (
                    <div className="flex items-center gap-2">
                      <Popover open={colorPickerOpen} onOpenChange={(_e, data) => setColorPickerOpen(data.open)}>
                        <PopoverTrigger disableButtonEnhancement>
                          <button
                            type="button"
                            title="Välj färg"
                            className="w-8 h-8 rounded border border-gray-300 cursor-pointer shrink-0 hover:ring-2 hover:ring-offset-1 hover:ring-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            style={{ backgroundColor: field.value || '#cccccc' }}
                          />
                        </PopoverTrigger>
                        <PopoverSurface className="p-3">
                          <ColorPickerField value={field.value} onChange={field.onChange} />
                        </PopoverSurface>
                      </Popover>
                      <span className="font-mono text-sm">{field.value || 'Ingen färg'}</span>
                      {field.value && (
                        <Button size="small" appearance="subtle" onClick={() => field.onChange('')}>Ta bort</Button>
                      )}
                    </div>
                  );
                }}
              />
            </Field>
            {/* <Field label="Dölj ramavtal: ">
              <Combobox
                multiselect={true}  
              >
                <Option>Option 1</Option>
                <Option>Option 2</Option>
                <Option>Option 3</Option>
              </Combobox>
            </Field> */}
          </form>}
          </DialogContent>
          <DialogActions>
            <Button onClick={props.onClose}>Avbryt</Button>
            <Button appearance="primary" type="submit" form="user-settings-form">Spara</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}

export default UserSettingsDialog;